import React, { lazy, Suspense } from 'react';
import { Box, Typography, Container, Paper } from '@mui/material';
import { BrowserRouter } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import LightMarketingPageContainer from './marketing/LightMarketingPageContainer';
import MarketingFooter from './marketing/MarketingFooter';
import { lightStyles } from '../styles/lightStyles';

const LightMarketingThemeProvider = lazy(() => import('../themes/LightMarketingThemeProvider'));

interface MaintenancePageProps {
  title: string;
  message: string;
  estimatedEnd: string | null;
  recovered?: boolean;
}

const MaintenancePage: React.FC<MaintenancePageProps> = ({ title, message, estimatedEnd, recovered = false }) => {

  const estimatedTimeText = (() => {
    if (!estimatedEnd) return null;
    try {
      const endDate = new Date(estimatedEnd);
      if (endDate <= new Date()) return 'any moment now';
      return formatDistanceToNow(endDate, { addSuffix: true });
    } catch {
      return null;
    }
  })();

  return (
    <Suspense fallback={null}>
      <LightMarketingThemeProvider>
        <BrowserRouter>
          <LightMarketingPageContainer>
            <Container maxWidth="lg" sx={{ py: 4, position: 'relative', minHeight: '80vh', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={lightStyles.loginContainer}>
                <Paper sx={{ ...lightStyles.loginCard, maxWidth: 500, border: '1px solid #E5E7EB', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', borderRadius: '16px' }}>
                  {recovered ? (
                    <>
                      <Typography sx={{ ...lightStyles.welcomeTitle, fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 2 }}>
                        We're back!
                      </Typography>
                      <Typography sx={lightStyles.welcomeSubtitle}>
                        Reloading...
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Box
                        component="img"
                        src="/assets/images/logo-standard.png"
                        alt="Moddy AI"
                        sx={{ width: { xs: '150px', sm: '200px' }, height: 'auto', mb: 3 }}
                      />
                      <Typography sx={{ ...lightStyles.welcomeTitle, fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 2 }}>
                        {title}
                      </Typography>
                      <Typography sx={{ ...lightStyles.welcomeSubtitle, mb: estimatedTimeText ? 2 : 3 }}>
                        {message}
                      </Typography>
                      {estimatedTimeText && (
                        <Typography sx={{ ...lightStyles.welcomeSubtitle, mb: 3, fontWeight: 500 }}>
                          Expected back {estimatedTimeText}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                        This page checks for updates automatically
                      </Typography>
                    </>
                  )}
                </Paper>
              </Box>
            </Container>
            <MarketingFooter />
          </LightMarketingPageContainer>
        </BrowserRouter>
      </LightMarketingThemeProvider>
    </Suspense>
  );
};

export default MaintenancePage;
